export type Priority = 'high' | 'medium' | 'low';
export type TaskStatus = 'todo' | 'in_progress' | 'completed' | 'review';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  department: string;
  activeTasks: number;
}

export interface Subtask {
  id: string;
  title: string;
  completed: boolean;
  assignee?: User;
  dueDate?: string;
}

export interface Comment {
  id: string;
  userId: string;
  text: string;
  timestamp: string;
  isSupervisor?: boolean;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: Priority;
  assignee: User;
  dueDate: string;
  createdDate: string;
  section: string;
  workspace: string;
  subtasks: Subtask[];
  comments: Comment[];
  attachments: { id: string; name: string; url: string; type: string }[];
  collaborators: User[];
  timeEstimate?: string;
  recurring?: boolean;
  likes: number;
  completedSubtasks: number;
  totalSubtasks: number;
}

export interface Workspace {
  id: string;
  name: string;
  icon?: string;
}

export const users: User[] = [
  { id: '1', name: 'Alice Johnson', email: 'alice@example.com', avatar: 'AJ', department: 'Engineering', activeTasks: 23 },
  { id: '2', name: 'Bob Smith', email: 'bob@example.com', avatar: 'BS', department: 'Design', activeTasks: 18 },
  { id: '3', name: 'Carol White', email: 'carol@example.com', avatar: 'CW', department: 'Product', activeTasks: 15 },
  { id: '4', name: 'David Lee', email: 'david@example.com', avatar: 'DL', department: 'Marketing', activeTasks: 12 },
  { id: '5', name: 'Emma Davis', email: 'emma@example.com', avatar: 'ED', department: 'Engineering', activeTasks: 19 },
  { id: '6', name: 'Frank Miller', email: 'frank@example.com', avatar: 'FM', department: 'Operations', activeTasks: 14 },
];

export const workspaces: Workspace[] = [
  { id: '1', name: 'Personal', icon: '👤' },
  { id: '2', name: 'Team Alpha', icon: '🚀' },
  { id: '3', name: 'Client X', icon: '💼' },
];

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);
const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1);
const nextWeek = new Date(today);
nextWeek.setDate(nextWeek.getDate() + 7);
const lastWeek = new Date(today);
lastWeek.setDate(lastWeek.getDate() - 7);

export const tasks: Task[] = [
  {
    id: '1',
    title: 'Design new dashboard layout',
    description: 'Create wireframes and high-fidelity mockups for the analytics dashboard redesign.',
    status: 'in_progress',
    priority: 'high',
    assignee: users[1],
    dueDate: tomorrow.toISOString().split('T')[0],
    createdDate: lastWeek.toISOString().split('T')[0],
    section: 'To Do',
    workspace: '2',
    subtasks: [
      { id: 's1', title: 'Create wireframes', completed: true, assignee: users[1] },
      { id: 's2', title: 'Design high-fidelity mockups', completed: true, assignee: users[1] },
      { id: 's3', title: 'Get stakeholder approval', completed: false, assignee: users[2] },
    ],
    comments: [
      { id: 'c1', userId: '1', text: 'Looking great! Love the color scheme.', timestamp: '2 hours ago', isSupervisor: true },
      { id: 'c2', userId: '2', text: 'Thanks! I\'ll have the final version ready by EOD.', timestamp: '1 hour ago' },
    ],
    attachments: [
      { id: 'a1', name: 'dashboard-wireframe.fig', url: '#', type: 'figma' },
      { id: 'a2', name: 'mockup-v2.png', url: '#', type: 'image' },
    ],
    collaborators: [users[0], users[1], users[2]],
    timeEstimate: '8h',
    recurring: false,
    likes: 3,
    completedSubtasks: 2,
    totalSubtasks: 3,
  },
  {
    id: '2',
    title: 'Implement authentication flow',
    description: 'Set up OAuth 2.0 authentication with social login providers.',
    status: 'in_progress',
    priority: 'high',
    assignee: users[0],
    dueDate: yesterday.toISOString().split('T')[0],
    createdDate: lastWeek.toISOString().split('T')[0],
    section: 'In Progress',
    workspace: '2',
    subtasks: [
      { id: 's4', title: 'Set up OAuth providers', completed: true },
      { id: 's5', title: 'Create login UI', completed: true },
      { id: 's6', title: 'Add session management', completed: false },
      { id: 's7', title: 'Write tests', completed: false },
      { id: 's8', title: 'Security audit', completed: false },
    ],
    comments: [],
    attachments: [],
    collaborators: [users[0], users[4]],
    timeEstimate: '16h',
    recurring: false,
    likes: 5,
    completedSubtasks: 2,
    totalSubtasks: 5,
  },
  {
    id: '3',
    title: 'Update API documentation',
    description: 'Add examples and improve endpoint descriptions in the developer docs.',
    status: 'todo',
    priority: 'medium',
    assignee: users[4],
    dueDate: nextWeek.toISOString().split('T')[0],
    createdDate: new Date(today.setDate(today.getDate() - 3)).toISOString().split('T')[0],
    section: 'To Do',
    workspace: '2',
    subtasks: [],
    comments: [],
    attachments: [],
    collaborators: [users[4]],
    timeEstimate: '4h',
    recurring: false,
    likes: 1,
    completedSubtasks: 0,
    totalSubtasks: 0,
  },
  {
    id: '4',
    title: 'Fix mobile responsive issues',
    description: 'Address layout problems on mobile devices across all pages.',
    status: 'todo',
    priority: 'high',
    assignee: users[4],
    dueDate: tomorrow.toISOString().split('T')[0],
    createdDate: yesterday.toISOString().split('T')[0],
    section: 'To Do',
    workspace: '1',
    subtasks: [
      { id: 's9', title: 'Test on iOS', completed: false },
      { id: 's10', title: 'Test on Android', completed: false },
    ],
    comments: [],
    attachments: [],
    collaborators: [users[4], users[1]],
    timeEstimate: '6h',
    recurring: false,
    likes: 2,
    completedSubtasks: 0,
    totalSubtasks: 2,
  },
  {
    id: '5',
    title: 'Prepare quarterly presentation',
    description: 'Create slides for Q2 business review meeting.',
    status: 'review',
    priority: 'medium',
    assignee: users[2],
    dueDate: nextWeek.toISOString().split('T')[0],
    createdDate: lastWeek.toISOString().split('T')[0],
    section: 'Review',
    workspace: '3',
    subtasks: [
      { id: 's11', title: 'Gather metrics', completed: true },
      { id: 's12', title: 'Create slides', completed: true },
      { id: 's13', title: 'Rehearse presentation', completed: false },
    ],
    comments: [
      { id: 'c3', userId: '3', text: 'Slides look good, just need to add the ROI metrics from last month.', timestamp: '3 hours ago' },
    ],
    attachments: [
      { id: 'a3', name: 'Q2-Review.pptx', url: '#', type: 'presentation' },
    ],
    collaborators: [users[2], users[3]],
    timeEstimate: '3h',
    recurring: true,
    likes: 0,
    completedSubtasks: 2,
    totalSubtasks: 3,
  },
  {
    id: '6',
    title: 'Code review for feature branch',
    description: 'Review pull request #234 for the new payment integration.',
    status: 'in_progress',
    priority: 'medium',
    assignee: users[0],
    dueDate: today.toISOString().split('T')[0],
    createdDate: yesterday.toISOString().split('T')[0],
    section: 'In Progress',
    workspace: '2',
    subtasks: [],
    comments: [],
    attachments: [],
    collaborators: [users[0], users[4]],
    timeEstimate: '2h',
    recurring: false,
    likes: 1,
    completedSubtasks: 0,
    totalSubtasks: 0,
  },
  {
    id: '7',
    title: 'Set up CI/CD pipeline',
    description: 'Configure GitHub Actions for automated testing and deployment.',
    status: 'todo',
    priority: 'low',
    assignee: users[4],
    dueDate: nextWeek.toISOString().split('T')[0],
    createdDate: new Date(today.setDate(today.getDate() - 5)).toISOString().split('T')[0],
    section: 'To Do',
    workspace: '2',
    subtasks: [
      { id: 's14', title: 'Configure test workflow', completed: false },
      { id: 's15', title: 'Set up deployment', completed: false },
      { id: 's16', title: 'Add status badges', completed: false },
    ],
    comments: [],
    attachments: [],
    collaborators: [users[4]],
    timeEstimate: '5h',
    recurring: false,
    likes: 0,
    completedSubtasks: 0,
    totalSubtasks: 3,
  },
  {
    id: '8',
    title: 'User research interviews',
    description: 'Conduct 5 user interviews to gather feedback on the new feature.',
    status: 'in_progress',
    priority: 'medium',
    assignee: users[2],
    dueDate: nextWeek.toISOString().split('T')[0],
    createdDate: lastWeek.toISOString().split('T')[0],
    section: 'In Progress',
    workspace: '1',
    subtasks: [
      { id: 's17', title: 'Schedule interviews', completed: true },
      { id: 's18', title: 'Conduct interviews (3/5)', completed: false },
      { id: 's19', title: 'Analyze findings', completed: false },
    ],
    comments: [],
    attachments: [],
    collaborators: [users[2], users[1]],
    timeEstimate: '10h',
    recurring: false,
    likes: 2,
    completedSubtasks: 1,
    totalSubtasks: 3,
  },
  {
    id: '9',
    title: 'Onboard new team member',
    description: 'Set up accounts and provide training for new hire starting Monday.',
    status: 'completed',
    priority: 'high',
    assignee: users[5],
    dueDate: yesterday.toISOString().split('T')[0],
    createdDate: lastWeek.toISOString().split('T')[0],
    section: 'Completed',
    workspace: '2',
    subtasks: [
      { id: 's20', title: 'Create accounts', completed: true },
      { id: 's21', title: 'Schedule orientation', completed: true },
      { id: 's22', title: 'Assign mentor', completed: true },
    ],
    comments: [],
    attachments: [],
    collaborators: [users[5]],
    timeEstimate: '4h',
    recurring: false,
    likes: 4,
    completedSubtasks: 3,
    totalSubtasks: 3,
  },
  {
    id: '10',
    title: 'Database migration',
    description: 'Migrate user data to new schema version 2.0.',
    status: 'completed',
    priority: 'high',
    assignee: users[0],
    dueDate: lastWeek.toISOString().split('T')[0],
    createdDate: new Date(lastWeek.setDate(lastWeek.getDate() - 7)).toISOString().split('T')[0],
    section: 'Completed',
    workspace: '2',
    subtasks: [],
    comments: [],
    attachments: [],
    collaborators: [users[0]],
    timeEstimate: '12h',
    recurring: false,
    likes: 6,
    completedSubtasks: 0,
    totalSubtasks: 0,
  },
];

export const analyticsData = {
  tasksCompleted: 47,
  completionRate: 78,
  avgTimeToComplete: 2.3,
  topPerformer: { user: users[0], tasksCompleted: 23 },
  completedTasksOverTime: [
    { date: '2026-04-07', completed: 2 },
    { date: '2026-04-14', completed: 4 },
    { date: '2026-04-21', completed: 3 },
    { date: '2026-04-28', completed: 5 },
    { date: '2026-05-05', completed: 6 },
  ],
  priorityBreakdown: [
    { priority: 'High', value: 40, color: '#EF4444' },
    { priority: 'Medium', value: 35, color: '#F59E0B' },
    { priority: 'Low', value: 25, color: '#3B82F6' },
  ],
};

export const notifications = [
  { id: 'n1', text: 'Alice Johnson completed "Database migration"', taskId: '10', timestamp: '5 min ago', read: false },
  { id: 'n2', text: 'Bob Smith mentioned you in a comment', taskId: '1', timestamp: '1 hour ago', read: false },
  { id: 'n3', text: 'Task "Implement authentication flow" is overdue', taskId: '2', timestamp: '2 hours ago', read: false },
];
